import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Spin } from '@robinpowered/ui-kit';
import { useAuthContext } from 'contexts';
import { useResolveOrgFromEvent } from 'hooks/useResolveOrgFromEvent';
import { useGetOrganizationsForSwitcherQuery } from 'generated/graphql';
import {
  buildEventUrl,
  CREATE_PREFILL_PARAM_KEYS,
  parseCreateEventSeed,
} from '@robinpowered/dashboard-apps-common';
import { PageMessage } from './PageMessage';

/**
 * Modes recognized by `LegacyEventRedirect`. Map 1:1 to the three bare URLs
 * the shell registers at its root:
 *
 *   - `event`               → `/event/:eventID`
 *   - `event-composer-edit` → `/event-composer/:eventID`
 *   - `event-composer-new`  → `/event-composer`
 *
 * Note: `/event/:eventID/series` is intentionally NOT supported. Recurring
 * series URLs continue to be served by the legacy AngularJS dashboard via
 * Cloudflare routing that excludes them from the shell bucket.
 */
export type LegacyEventRedirectMode =
  | 'event'
  | 'event-composer-edit'
  | 'event-composer-new';

type Props = {
  mode: LegacyEventRedirectMode;
};

/**
 * Catches the three bare event URLs at the shell's root, resolves the
 * matching org for the user, and redirects to the canonical `event`
 * query-param form under the office MFE.
 */
export const LegacyEventRedirect = ({ mode }: Props): JSX.Element => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { eventID } = useParams<{ eventID: string }>();
  const { currentOrg } = useAuthContext();

  // For event/edit modes we resolve the org via the REST event probe.
  // For the create-new mode we have no event to probe — fall back to the
  // user's first org from `organizationsForSwitcher` (matching the pattern
  // `HomepageRedirect` uses when no orgSlug is in the URL). `currentOrg`
  // is null on a bare `/event-composer` URL because `organizationBySlug`
  // requires an `orgSlug` route param.
  const needsProbe = mode !== 'event-composer-new';

  // The legacy dashboard appends `?org=<active-org-slug>` so the shell opens in
  // the org the user was actually working in, not just their default. Honored
  // for EVERY mode:
  //   - create: it's the only org signal (there's no event to probe).
  //   - event / edit: it takes precedence over the event probe. The probe
  //     derives the org from `space.location.account_id`, but events with no
  //     Robin space (personal or calendar-synced meetings) have none, so the
  //     probe silently falls back to the user's *first* org and lands the user
  //     in the wrong org. The explicit `?org=` is the user-intended target; the
  //     probe still runs for best-effort grid (date/office) seeding.
  const requestedOrgSlug = useMemo(
    () => new URLSearchParams(location.search).get('org') || null,
    [location.search]
  );

  const {
    orgSlug: resolvedOrgSlug,
    locationId: resolvedLocationId,
    eventStartDateTime,
    eventTimeZone,
    loading: probeLoading,
    error: probeError,
  } = useResolveOrgFromEvent(needsProbe ? eventID : undefined);

  const {
    data: switcherData,
    loading: switcherLoading,
    error: switcherError,
  } = useGetOrganizationsForSwitcherQuery({
    variables: { first: 1 },
    // No need to look up the user's default org when the create URL handed us one.
    skip: needsProbe || !!requestedOrgSlug,
  });
  const switcherOrgSlug =
    switcherData?.getCurrentUserAndOrgs?.organizationsForSwitcher
      ?.organizations[0]?.slug ?? null;

  // Prefer an org explicitly requested on the create URL, then the orgSlug
  // already in `AuthContext` (set when the user navigated here from within an
  // org), then fall back to the switcher's first org.
  const fallbackOrgSlug =
    requestedOrgSlug ?? currentOrg?.slug ?? switcherOrgSlug;

  // Probe modes: an explicit `?org=` wins over the probe-resolved org;
  // otherwise fall back to what the probe resolved. Create mode uses the
  // fallback chain above.
  const targetOrgSlug = needsProbe
    ? requestedOrgSlug ?? resolvedOrgSlug
    : fallbackOrgSlug;
  const loading = needsProbe
    ? probeLoading
    : switcherLoading && !fallbackOrgSlug;
  // Memoize the derived error so it has a stable identity across renders,
  // otherwise the useEffect deps below see a fresh `Error` instance every
  // render and react-hooks/exhaustive-deps flags it.
  const error = useMemo<Error | null>(() => {
    // A probe failure is only fatal when we have no explicitly-requested org to
    // fall back on. When the legacy dashboard handed us `?org=`, we already know
    // which org to open in, so a failed probe just costs us the grid date/office
    // seeding — we still redirect into the requested org and let the shell
    // composer fetch the event with proper tenant context.
    if (probeError && !requestedOrgSlug) return probeError;
    if (!needsProbe && switcherError) {
      return new Error(
        `Failed to load user's organizations: ${switcherError.message}`
      );
    }
    if (!needsProbe && !loading && !targetOrgSlug) {
      return new Error(
        'Cannot create event: no active organization in context'
      );
    }
    return null;
  }, [
    needsProbe,
    loading,
    targetOrgSlug,
    probeError,
    switcherError,
    requestedOrgSlug,
  ]);

  useEffect(() => {
    if (loading || error || !targetOrgSlug) {
      return;
    }

    // For event-bound modes, also seed `currentDate` + `locationId` from the
    // probe so the meeting-rooms grid lands on the event's date and office
    // instead of the user's last-viewed state. The schedule MFE reads both
    // params in `CalendaredLocationsContext` (see `useQueryParams` there).
    const basePath = `/${targetOrgSlug}/schedule/meeting-rooms`;
    const grid =
      mode === 'event-composer-new'
        ? null
        : buildGridQueryParams({
            eventStartDateTime,
            eventTimeZone,
            locationId: resolvedLocationId,
          });

    let target: string;
    switch (mode) {
      case 'event':
        if (!eventID) return;
        target = buildEventUrl(basePath, { mode: 'details', eventId: eventID });
        break;
      case 'event-composer-edit':
        if (!eventID) return;
        target = buildEventUrl(basePath, { mode: 'edit', eventId: eventID });
        break;
      case 'event-composer-new': {
        // Carry the create-composer prefill context (space, date/time, etc.) that the legacy
        // dashboard serialized onto the `/event-composer` URL through to the canonical
        // meeting-rooms create URL, where `EventOverlayHost` reads it. Edit/detail modes don't
        // need this - they re-derive their context from the event probe above.
        const incomingParams = new URLSearchParams(location.search);
        target = buildEventUrl(
          basePath,
          { mode: 'create' },
          parseCreateEventSeed(incomingParams)
        );
        const prefill = pickCreatePrefillParams(incomingParams);
        if (Object.keys(prefill).length > 0) {
          target = appendQueryParams(target, prefill);
        }
        break;
      }
    }

    if (grid) target = appendQueryParams(target, grid);

    navigate(target, { replace: true });
  }, [
    loading,
    error,
    targetOrgSlug,
    mode,
    eventID,
    navigate,
    resolvedLocationId,
    eventStartDateTime,
    eventTimeZone,
    location.search,
  ]);

  if (error) {
    return (
      <PageMessage
        title={t('errors.event_not_found.title', 'Event not found')}
        message={t(
          'errors.event_not_found.message',
          "We couldn't open that event. The link may be invalid, or you may not have access."
        )}
      />
    );
  }

  return (
    <CenteredContainer>
      <Spin size="large" />
    </CenteredContainer>
  );
};

// Formats the meeting-rooms grid query params from the probe response.
// `currentDate` is YYYY-MM-DD in the event's own timezone so the grid lands
// on the same calendar day the event lives on, regardless of the viewer's
// browser timezone. Returns null when nothing useful is available.
const buildGridQueryParams = ({
  eventStartDateTime,
  eventTimeZone,
  locationId,
}: {
  eventStartDateTime: string | null;
  eventTimeZone: string | null;
  locationId: number | null;
}): Record<string, string> | null => {
  const out: Record<string, string> = {};
  if (eventStartDateTime) {
    const m = eventTimeZone
      ? moment.tz(eventStartDateTime, eventTimeZone)
      : moment(eventStartDateTime);
    if (m.isValid()) out.currentDate = m.format('YYYY-MM-DD');
  }
  if (locationId !== null) out.locationId = String(locationId);
  return Object.keys(out).length > 0 ? out : null;
};

// Forward the non-seed create-composer prefill params that `EventOverlayHost`
// reads directly from the meeting-rooms URL. The seed params (started_at /
// ended_at / space_id / level_id) are handled by `parseCreateEventSeed` +
// `buildEventUrl`; these are forwarded verbatim from the legacy
// `/event-composer` URL so a redirected create open keeps the same prefilled
// values. `CREATE_PREFILL_PARAM_KEYS` is the shared source of truth — the same
// list `clearCreateEventParams` strips on close — so the two never drift.
const pickCreatePrefillParams = (
  params: URLSearchParams
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const key of CREATE_PREFILL_PARAM_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') {
      out[key] = value;
    }
  }
  return out;
};

const appendQueryParams = (
  url: string,
  extra: Record<string, string>
): string => {
  const [path, existing = ''] = url.split('?');
  const params = new URLSearchParams(existing);
  for (const [k, v] of Object.entries(extra)) params.set(k, v);
  return `${path}?${params.toString()}`;
};

const CenteredContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
